import React, { useEffect, useState, useRef } from 'react';
import styles from './TutorialPopover.module.css';
import { ButtonSubmitGreen } from '../Buttons/Buttons';
import { setTutorialTipMeta } from '../../../utils/tutorialApi';
import CloseIcon from '@mui/icons-material/Close';

/**
 * TutorialPopover
 * Props:
 * - show: boolean
 * - title: string
 * - description: string (short text)
 * - anchorRect: DOMRect (optional) OR anchorRef: ref to element
 * - direction: 'top'|'bottom' (preferred arrow side) - optional
 * - theme: 'auto'|'light'|'dark' (auto will pick by prefers-color-scheme)
 * - onClose: fn
 * - className, style overrides
 */
const TutorialPopover = ({
  show = true,
  tipId = null,
  title = 'Tip',
  description = '',
  anchorRect = null,
  anchorRef = null,
  direction = 'bottom',
  theme = 'auto',
  onClose = () => {},
  onPrimary = () => {},
  primaryLabel = 'Try it',
  style = {},
  className = '',
}) => {
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const [effectiveDir, setEffectiveDir] = useState(direction);
  const [dontShow, setDontShow] = useState(false);
  const [arrowLeft, setArrowLeft] = useState(22);
  const containerRef = useRef(null);
  const titleId = `tutorial-title-${tipId || 'default'}`;
  const descId = `tutorial-desc-${tipId || 'default'}`;

  useEffect(() => {
    const compute = () => {
      let rect = anchorRect;
      if (!rect && anchorRef && anchorRef.current) rect = anchorRef.current.getBoundingClientRect();

      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // default assumed popover width
      const assumedWidth = Math.min(360, Math.max(200, Math.floor(vw * 0.9)));

      if (!rect) {
        // no anchor: place top-left with some margin (fixed to viewport)
        setEffectiveDir(direction);
        setPos({ left: 16, top: 16 });
        setArrowLeft(22);
        return;
      }

      // decide whether to place above or below depending on space (use viewport coords)
      const spaceAbove = rect.top;
      const spaceBelow = vh - rect.bottom;
      let chosenDir = direction === 'auto' ? (spaceBelow >= spaceAbove ? 'bottom' : 'top') : direction;

      // compute tentative left so popover centers on anchor
      const popWidth = containerRef.current ? containerRef.current.offsetWidth : assumedWidth;
      const leftUnbounded = rect.left + rect.width / 2 - popWidth / 2;
      const left = Math.max(8, Math.min(leftUnbounded, vw - popWidth - 8));

      // compute top based on chosenDir (fixed -> use rect coordinates directly)
      let top;
      const gap = 10;
      if (chosenDir === 'bottom') {
        top = rect.bottom + gap;
        // if not enough space below, flip
        const popH = containerRef.current ? containerRef.current.offsetHeight : 140;
        if (top + popH > vh - 8 && spaceAbove > spaceBelow) {
          chosenDir = 'top';
          top = rect.top - gap - (containerRef.current ? containerRef.current.offsetHeight : 140);
        }
      } else {
        top = rect.top - gap - (containerRef.current ? containerRef.current.offsetHeight : 140);
        // if not enough space above, flip
        if (top < 8 && spaceBelow > spaceAbove) {
          chosenDir = 'bottom';
          top = rect.bottom + gap;
        }
      }

      // clamp top to viewport
      const popHfinal = containerRef.current ? containerRef.current.offsetHeight : 140;
      const minTop = 8;
      const maxTop = vh - popHfinal - 8;
      if (top < minTop) top = minTop;
      if (top > maxTop) top = maxTop;

      setEffectiveDir(chosenDir);
      setPos({ left, top });

      // compute arrow left relative to popover
      const centerX = rect.left + rect.width / 2;
      const rel = Math.round(centerX - left - 10); // 10 ~ half arrow width
      const clamped = Math.max(8, Math.min(rel, Math.max(20, popWidth - 20)));
      setArrowLeft(clamped);
    };

    // run initially and on scroll/resize to follow anchor
    compute();
    // ensure we recompute after paint so measurements (offsetWidth/Height) are accurate
    const raf = requestAnimationFrame(() => compute());
    const onScroll = () => compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', onScroll);
      cancelAnimationFrame(raf);
    };
  }, [anchorRect, anchorRef, direction, show]);

  // Close if persisted preference says don't show
  useEffect(() => {
    if (dontShow) {
      // persist preference on server and notify parent to hide
      (async () => {
        try { if (tipId) await setTutorialTipMeta(tipId, { dontShow: true }); } catch (_) {}
        onClose();
      })();
    }
  }, [dontShow, onClose, tipId]);

  // accessibility: focus transfer + escape + focus trap
  useEffect(() => {
    if (!show || dontShow) return;

    const el = containerRef.current;
    if (!el) return;

    // Only focus the popover if the user is not actively typing in an input/textarea/select
    try {
      const active = document.activeElement;
      const tag = active && active.tagName && active.tagName.toLowerCase();
      const isTypingElement = tag === 'input' || tag === 'textarea' || tag === 'select' || (active && active.isContentEditable);
      if (!isTypingElement) {
        el.setAttribute('tabindex', '-1');
        try { el.focus({ preventScroll: true }); } catch { el.focus(); }
      }
    } catch (_) {}

    const handleKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }

      if (e.key === 'Tab') {
        // simple focus trap
        const focusable = el.querySelectorAll('a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])');
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [show, dontShow, onClose]);

  // theme auto-detect
  const effectiveTheme = theme === 'auto' ? (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : theme;

  // hide entirely if preference says don't show
  if (!show || dontShow) return null;

  const containerStyle = {
    left: pos.left,
    top: pos.top,
    '--arrow-left': `${arrowLeft}px`,
    ...style,
  };

  const arrowClass = effectiveDir === 'top' ? styles.arrowTop : styles.arrowBottom;
  const isDarkClass = effectiveTheme === 'dark' ? styles.isDark : '';

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${styles[effectiveTheme]} ${isDarkClass} ${arrowClass} BubblePoppin ${!show ? styles.hidden : ''} ${className}`}
      style={containerStyle}
      role="dialog"
      aria-labelledby={titleId}
      aria-describedby={descId}
    >
      <div className={styles.inner}>
        <div className={styles.header}>
          <div id={titleId} className={styles.title}>{title}</div>
          <div className={styles.closeBtnWrap}>
            <button
              aria-label="Close tutorial"
              className={styles.smallClose}
              onClick={async () => {
                // Close without persisting "dontShow". The "Don't show again" checkbox
                // controls persistence so sequence of tips can continue when user
                // simply closes a tip.
                try { onClose && onClose(); } catch (er) {}
              }}
            >
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>
        <div id={descId} className={styles.desc}>{description}</div>

        <div className={styles.footer}>
          <div className={styles.leftActions}>
            <label className={styles.dontShowChk}>
              <input
                type="checkbox"
                checked={dontShow}
                onChange={async (e) => {
                  const checked = e.target.checked;
                  setDontShow(checked);
                  try {
                    if (tipId) {
                      await setTutorialTipMeta(tipId, { dontShow: !!checked });
                    }
                  } catch (er) {
                    // ignore
                  }
                }}
              />
              Don't show again
            </label>
          </div>

          <div className={styles.tryBtnWrap}>
            <ButtonSubmitGreen onClick={() => {
                // Close the popover first, then run primary action after a short delay
                try { onClose && onClose(); } catch (er) {}
                setTimeout(async () => {
                  try {
                    if (tipId) {
                      try { await setTutorialTipMeta(tipId, { recordShown: true }); } catch (_) {}
                    }
                  } catch (er) {}
                  try { onPrimary && onPrimary(); } catch (er) {}
                }, 160);
              }}>{primaryLabel}</ButtonSubmitGreen>
          </div>
        </div>
      </div>

      <div className={styles.srOnly} aria-live="polite">{title} tip opened.</div>
    </div>
  );
};

export default TutorialPopover;
